import { createContext, useContext, type ReactNode } from 'react'

import type { EditorMode } from './types/editor-mode.types'
import type { Block } from '@w/content-blocks'
import type { AnswerPractice } from './types/block-editor.types'
export type { EditorMode } from './types/editor-mode.types'

/**
 * Answer-mode context for training. `activeQid` is the qid of the flashcard
 * being trained right now; `clozeOrder` is the document order of every cloze
 * qid in the rendered card. Together they let a cloze decide whether it is
 * before / is / after the active cloze (see cloze rendering rule in
 * markdown-node). In 'edit' mode these are empty and unused.
 */
export interface EditorModeValue {
  mode: EditorMode
  activeQid?: string
  clozeOrder: string[]
}

const EditorModeContext = createContext<EditorMode>('edit')
const ActiveQidContext = createContext<string | undefined>(undefined)
const AnswerAutoFocusContext = createContext(true)

export function useAnswerAutoFocus() {
  return useContext(AnswerAutoFocusContext)
}
const ClozeOrderContext = createContext<string[]>([])

const ClozeHintFailContext = createContext<((qid: string) => void) | undefined>(
  undefined,
)
const EMPTY_CLOZE_ORDER: string[] = []

export function ClozeHintFailProvider({
  onFail,
  children,
}: {
  onFail?: (qid: string) => void
  children: ReactNode
}) {
  return (
    <ClozeHintFailContext.Provider value={onFail}>
      {children}
    </ClozeHintFailContext.Provider>
  )
}

export function useClozeHintFail() {
  return useContext(ClozeHintFailContext)
}

const AnswerPracticeContext = createContext<AnswerPractice | undefined>(undefined)
/** Each question's parts (by qid) in document order, hidden blocks left out. */
export type PracticeLayout = { parts: ReadonlyMap<string, readonly Block[]> }
const EMPTY_LAYOUT: PracticeLayout = { parts: new Map() }
const PracticeLayoutContext = createContext<PracticeLayout>(EMPTY_LAYOUT)

export function AnswerPracticeProvider({ practice, layout = EMPTY_LAYOUT, children }: {
  practice?: AnswerPractice
  layout?: PracticeLayout
  children: ReactNode
}) {
  return <AnswerPracticeContext value={practice}><PracticeLayoutContext value={layout}>{children}</PracticeLayoutContext></AnswerPracticeContext>
}

export function useAnswerPractice() {
  return useContext(AnswerPracticeContext)
}

/**
 * The host's controls for this part's question, when this part is the question's last one: they render inline right
 * after it (after a blank, at the end of a question card), so each question's buttons sit beside that question.
 */
export function usePracticeControls(block: { id: string; qid: string }): ReactNode {
  const practice = useContext(AnswerPracticeContext)
  const layout = useContext(PracticeLayoutContext)
  const parts = layout.parts.get(block.qid)
  if (!practice || parts?.at(-1)?.id !== block.id) return null
  return practice.controls(block.qid, parts)
}

/** Whether a part shows its answer, and its mark: the block's runtime fields (`$showAnswer`, `$mark`), or the host practice marks. */
export function useAnswerReveal(block: { id: string; $showAnswer?: boolean; $mark?: number }): { showAnswer: boolean; mark?: number } {
  const practice = useContext(AnswerPracticeContext)
  const practiced = practice?.marks[block.id]
  if (practiced !== undefined) return { showAnswer: true, mark: practiced ?? undefined }
  return { showAnswer: block.$showAnswer === true, mark: block.$mark }
}

export function EditorModeProvider({
  mode,
  activeQid,
  answerAutoFocus = true,
  clozeOrder = EMPTY_CLOZE_ORDER,
  children,
}: {
  mode: EditorMode
  activeQid?: string
  answerAutoFocus?: boolean
  clozeOrder?: string[]
  children: ReactNode
}) {
  return (
    <EditorModeContext.Provider value={mode}>
      <ActiveQidContext.Provider value={activeQid}>
        <AnswerAutoFocusContext value={answerAutoFocus}>
          <ClozeOrderContext.Provider value={clozeOrder}>{children}</ClozeOrderContext.Provider>
        </AnswerAutoFocusContext>
      </ActiveQidContext.Provider>
    </EditorModeContext.Provider>
  )
}

export function useEditorModeValue(): EditorModeValue {
  return { mode: useContext(EditorModeContext), activeQid: useContext(ActiveQidContext), clozeOrder: useContext(ClozeOrderContext) }
}

/**
 * Decide how a cloze should render in answer mode relative to the active cloze:
 * - 'before'  → cloze that precedes the active one in document order
 * - 'active'  → the cloze being trained right now (gets the input / reveal)
 * - 'after'   → cloze that follows the active one
 * When there is no active qid (e.g. a non-cloze flashcard, or the active qid is
 * not a cloze) every cloze is treated as 'active' so single-cloze cards and the
 * page editor keep working unchanged.
 */
export function clozePosition(
  ctx: EditorModeValue,
  clozeQid: string,
): 'before' | 'active' | 'after' {
  const { activeQid, clozeOrder } = ctx
  if (activeQid === undefined) return 'active'
  if (clozeQid === activeQid) return 'active'
  const activeIndex = clozeOrder.indexOf(activeQid)
  // If the active qid is not a cloze in this card, fall back to active so the
  // single visible cloze (if any) still renders as an input.
  if (activeIndex === -1) return 'active'
  const thisIndex = clozeOrder.indexOf(clozeQid)
  if (thisIndex === -1) return 'after'
  return thisIndex < activeIndex ? 'before' : 'after'
}
